import { isUserRole, type UserRole } from "@platform/user";
import { type SessionEndReason, UserProvider } from "@platform/user/client";
import { type ReactNode, useCallback } from "react";
import { useNavigate } from "react-router";

import { clearBrokerImpersonation } from "../auth/auth0Pkce";
import { SESSION_END_REASON_STATE } from "../auth/sessionEnd";
import envVariables from "../projectEnvVariables";

interface SessionEnvVariables {
  readonly VITE_REACT_APP_API_PATH: string;
  readonly VITE_REACT_APP_AUTH0_DOMAIN: string;
  readonly VITE_REACT_APP_AUTH0_CLIENT_ID: string;
}

// projectEnvVariables.js is untyped JavaScript; these are the only values read here.
const sessionEnv = envVariables as SessionEnvVariables;

// Dev-only role override, compiled out of production builds and kept out of test runs.
const localRoleOverride = import.meta.env.VITE_LOCAL_USER_ROLE_OVERRIDE as
  | string
  | undefined;
const roleOverride: UserRole | undefined =
  import.meta.env.DEV &&
  !import.meta.env.VITEST &&
  !import.meta.env.VITE_PLAYWRIGHT &&
  isUserRole(localRoleOverride)
    ? localRoleOverride
    : undefined;

const sessionConfig = {
  apiBaseUrl: sessionEnv.VITE_REACT_APP_API_PATH,
  // The audience matches the login client in auth/auth0Pkce.ts, so both share the SDK's cache.
  sso: {
    domain: sessionEnv.VITE_REACT_APP_AUTH0_DOMAIN,
    clientId: sessionEnv.VITE_REACT_APP_AUTH0_CLIENT_ID,
    audience: sessionEnv.VITE_REACT_APP_AUTH0_CLIENT_ID,
  },
};

/** The user lib's session for bpfe: on a session's end, back to the login page with the reason. */
const SessionProvider = ({ children }: { children: ReactNode }): ReactNode => {
  const navigate = useNavigate();
  const onSessionEnd = useCallback(
    (reason: SessionEndReason) => {
      // The impersonation mark describes the session that just ended, so the next
      // person to sign in on this browser must not inherit Exit Broker View.
      clearBrokerImpersonation();
      void navigate("/", { state: { [SESSION_END_REASON_STATE]: reason } });
    },
    [navigate],
  );

  return (
    <UserProvider
      config={sessionConfig}
      roleOverride={roleOverride}
      onSessionEnd={onSessionEnd}
    >
      {children}
    </UserProvider>
  );
};

export default SessionProvider;
